import { useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { getMemo, getMemoHtml } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { LoadingState, EmptyState, ErrorState } from "@/components/ui/States";
import { Pill } from "@/components/ui/Pill";

type Props = { securityId: number };

export function MemoViewer({ securityId }: Props) {
  const meta = useQuery({
    queryKey: ["memo", securityId, "meta"],
    queryFn: () => getMemo(securityId),
    retry: false,
  });
  const html = useQuery({
    queryKey: ["memo", securityId, "html"],
    queryFn: () => getMemoHtml(securityId),
    enabled: meta.isSuccess,
    retry: false,
  });

  if (meta.isLoading) return <LoadingState message="Loading memo…" />;
  if (meta.isError) {
    return (
      <EmptyState
        title="No memo yet"
        description="Generate one from the Company page header. Memos summarise scores, citations, and the active stance."
      />
    );
  }
  if (html.isLoading) return <LoadingState message="Rendering memo…" />;
  if (html.isError || !html.data) {
    return <ErrorState detail="Memo HTML could not be loaded." />;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {meta.data?.ai_assisted ? (
          <Pill tone="accent">
            <Sparkles size={10} className="mr-1" />
            AI-assisted
          </Pill>
        ) : (
          <Pill tone="muted">Template-only</Pill>
        )}
        {meta.data?.llm_model && (
          <Pill tone="ok">model: {meta.data.llm_model}</Pill>
        )}
        <span className="text-2xs text-foreground-muted">
          generated {fmtDateTime(meta.data?.created_at)}
        </span>
      </div>
      <article
        className="memo-html prose prose-invert max-w-none text-sm"
        // The HTML is produced by our trusted backend renderer with HTML-escape.
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: html.data }}
      />
    </div>
  );
}
